import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, Button, Row, Col } from "react-bootstrap";
import { useSelector, useDispatch } from 'react-redux';
import { Link } from "react-router-dom";
import { getCompanyIdFromRoute } from 'services/redux/selectors/company';
import { Date } from 'core-js';
import { loadCompanyAssetLogsStart } from 'services/redux/actions/serviceDesk';
import { Tooltip } from 'components/Common/Tooltip/Tooltip';
import { useWindowSize } from 'components/Common/CustomHooks/useWindowSize';

import Axios from '../../../../../../node_modules/axios/index';

import conxtdOut from 'apis/conxtdOut';

import Icon from 'components/Icons/Icon';

import CustomProgressBar from '../CustomProgressBar';
import DataTableWrapper from './DataTableWrapper';
import Countdown from './Countdown';
import ProgressSection from './ProgressSection';
import LogViewPage from './LogViewPage';

import './CompanyAssetLogs.scss';

function CompanyAssetLogs() {
  const darkMode = useSelector(state => state.user.darkMode);
  const companyId = useSelector(getCompanyIdFromRoute);

  const windowSize = useWindowSize();

  // redux --------->
  const dispatch = useDispatch();

  const logsStore = useSelector(state => state.serviceDesk.assets.companyAssetImportLogs?.data) // selects data object for companyAssetImportLogs
  const pagination = logsStore?.pagination;
  const totalLogs = pagination?.count;
  const logsIndexData = logsStore?.logs;
  // ---------------|

  // progress tracking state: -------------------->
  // in progress ->
  const [importProgress, setImportProgress] = useState([]);
  const [exportProgress, setExportProgress] = useState([]);
  const [deleteProgress, setDeleteProgress] = useState([]);
  // -----------------------
  // in queue ->
  const [importQueued, setImportQueued] = useState([]);
  const [exportQueued, setExportQueued] = useState([]);
  const [deleteQueued, setDeleteQueued] = useState([]);
  // -----------------------
  // establishes if any processes are in progress ->
  const [activeProcess, setActiveProcess] = useState(false);
  // -----------------------
  // ----------------------------------------------
  
  const [countValue, setCountValue] = useState(2);
  const [triggerCall, setTriggerCall] = useState(false);
  const [delayCall, setDelayCall] = useState(true); // delays initial call to give backend time to receive request
  
  const [viewUid, setViewUid] = useState(null);
  const [allPercentages, setAllPercentages] = useState([]);
  const [viewPercentage, setViewPercentage] = useState(null);
  const [showLogViewPage, setShowLogViewPage] = useState(false);

  const handleShowModal = () => {
    setShowLogViewPage(true);
  }
  
  const handleHideModal = () => {
    setShowLogViewPage(false);
  }

  const uidStatus = useMemo(() => {
    const map = {};
    logsIndexData.forEach(row => {
      if (row.uid != null) {

        const status = row.status;
        let currentStatus = null;
        let currentIcon = null;
        let code = null;

        switch (status) {
          case "C":
            code = "C";
            currentStatus = "Completed";
            currentIcon = {
              icon:  "check-circle",
              colour: "green",
              description: 'Completed successfully',
            };
            break;
          
          case "CE":
            code = "CE";
            currentStatus = "Completed";
            currentIcon = {
              icon:  "exclamation-circle",
              colour: "red",
              description: 'Completed with errors',
            };
            break;

          case "N":
            code = "N";
            currentIcon = {
              icon:  "spinner",
              colour: "yellow",
              description: "In Progress",
            };
            currentStatus = "In Progress";
            break;

          // case null:
          //   code = null;
          //   currentStatus = "Status unavailable";
          //   break;
        
          default:
            break;
        }

        map[row.uid] = {status: currentStatus, icon: currentIcon, code};
      }
    });

    return map;
  }, [logsIndexData]);

  // sets active processes and considers invalid total < processed scenarios --------------------------------->
  // const allInProgress = [importProgress, exportProgress, deleteProgress];
  // const allInQueue = [importQueued, exportQueued, deleteQueued];
  
  const allInProgress = useMemo(() => {
    return [importProgress, exportProgress, deleteProgress].flat();
  }, [importProgress, exportProgress, deleteProgress]);
  
  const allInQueue = useMemo(() => {
    return [importQueued, exportQueued, deleteQueued].flat();
  }, [importQueued, exportQueued, deleteQueued]);
  
  const flatProgresses = useMemo(() => {
    return allInProgress.flat();
  }, [allInProgress]);

  useEffect(() => {
    const hasAnyProgress = Array.isArray(allInProgress) && allInProgress.length > 0;
    const hasAnyQueued = Array.isArray(allInQueue) && allInQueue.length > 0;
    const isActive = hasAnyProgress || hasAnyQueued;

    setActiveProcess(isActive);
    setCountValue(isActive ? 5 : 60);

  }, [
    allInProgress,
    allInQueue,
    flatProgresses,
  ]);
  // --------------------------------------------------------------------------------------------------------|
  
  const calculatePercentage = useCallback(( total, processed ) => {
    if (total && processed) {
      return Math.ceil(processed / total * 100)
    }
  }, [importProgress, exportProgress, deleteProgress])

  const getProcessedRows = (cancelled = false) => {
    const source = Axios.CancelToken.source();
    conxtdOut.get(`/company-assets-bulk/processed-rows/${companyId}`, { cancelToken: source.token })
      .then((res) => {
        const data = res?.data;

        if (!data) { return };

        const {
          multiple_categories_import,
          single_category_import,
          export_filtered,
          export_category,
          bulk_asset_delete,
         } = data;

        const importData = [multiple_categories_import, single_category_import].flat();
        const exportData = [export_filtered, export_category].flat();
        const deleteData = bulk_asset_delete;

        if (!cancelled) {
          setImportProgress(importData?.filter(i => i.state === "P"));
          setImportQueued(importData?.filter(i => i.state === "Q"));

          setExportProgress(exportData?.filter(i => i.state === "P"));
          setExportQueued(exportData?.filter(i => i.state === "Q"));
          
          setDeleteProgress(deleteData?.filter(i => i.state === "P"));
          setDeleteQueued(deleteData?.filter(i => i.state === "Q"));
        }
    });

    return () => {
      source.cancel("Component unmounted: request cancelled");
    }
  }

  useEffect(() => {
    const progresses = [importProgress, exportProgress, deleteProgress];
    const flattened = progresses.flat();

    if (flattened.length <= 0) { return };
    
    // imports
    const importTotal = importProgress?.[0]?.totals?.total;
    const importProcessed = importProgress?.[0]?.totals?.processed;
    const importUid = importProgress?.[0]?.uid ?? null;

    // exports
    const exportTotal = exportProgress?.[0]?.totals?.total;
    const exportProcessed = exportProgress?.[0]?.totals?.processed;
    const exportUid = exportProgress?.[0]?.uid ?? null;

    // delete
    const deleteTotal = deleteProgress?.[0]?.totals?.total;
    const deleteProcessed = deleteProgress?.[0]?.totals?.processed;
    const deleteUid = deleteProgress?.[0]?.uid ?? null;

    const importPercentage = importTotal && importProcessed ? calculatePercentage(importTotal, importProcessed) : null;
    const exportPercentage = exportTotal && exportProcessed ? calculatePercentage(exportTotal, exportProcessed) : null;
    const deletePercentage = deleteTotal && deleteProcessed ? calculatePercentage(deleteTotal, deleteProcessed) : null;
    
    // sets all in-progress percentages for each type of process
    setAllPercentages([
      {
        type: 'import',
        percentage: (importPercentage && activeProcess) ? importPercentage : null,
        uid: importUid,
      },
      {
        type: 'export',
        percentage: (exportPercentage && activeProcess) ? exportPercentage : null,
        uid: exportUid,
      },
      {
        type: 'delete',
        percentage: (deletePercentage && activeProcess) ? deletePercentage : null,
        uid: deleteUid,
      },
    ]);
  }, [
    calculatePercentage, 
    importProgress,
    exportProgress,
    deleteProgress,
    activeProcess,
    triggerCall,
    viewUid,
  ])

  useEffect(() => {
    if (!viewUid) { return };

    // find and set viewPercentage
    const process = allPercentages.find(i => i?.uid === viewUid);
    setViewPercentage(process?.percentage ?? null);
  }, [
    viewUid, 
    allPercentages,
    allPercentages?.import?.percentage, 
    allPercentages?.export?.percentage, 
    allPercentages?.delete?.percentage
  ])

  useEffect(() => {
    if (companyId && triggerCall) {
      getProcessedRows();
      return;
    }
  }, [companyId, triggerCall])

  const missingData = () => {
    return <span>N/A</span>
  }

  useEffect(() => {
    dispatch(loadCompanyAssetLogsStart())
  }, [activeProcess, flatProgresses?.length])

  // returns the correct text based on the source
  const getProcessTypeText = (data) => {
    const types = {
      'import': data?.update ? "Update Existing" : "Create New",
      'export': 'Export Filtered',
      'delete': 'Delete',
    }

    if (!data?.source) { return };
    // temporary solution to finding correct process type from data.source
    const findType = Object.keys(types).filter(item => data?.source?.toLowerCase().includes(item));
    const type = Array.isArray(findType) 
      ?
        findType.length > 1 ? 'delete' : findType
      : [];

    // let type = "";
    // switch (data?.source?.toLowerCase()) {
    //   case "delete":
    //     type = 'delete';
    //     break;
      
    //   case "import":
    //     type = 'import';
    //     break;
    
    //   case "export":
    //     type = 'export';
    //     break;
    
    //   default:
    //     break;
    // }
    // console.log('type: ', type)
    
    return types[type];
  }
  // --------------------------------------------|
  
  // locall stores previous data table state for backend-pagination for log view 
  // to avoid stale pagination being used in new dispatches
  const [dataTableState, setdataTableState] = useState({});
  
    const handleStorePaginationState = (dataTableState) => {
      setdataTableState(dataTableState ?? {});
    }
  // --------------------------------------------|

  const columnHeaders = [
    {
      key: "Type",
      Header: "Type",
      id: "type",
      filterable: false,
      // width: 200,
      accessor: data => {
        const type = getProcessTypeText(data);

        return (
          type ?
            <span className='data-table-cell'>{type}</span>
          :
            <span className='data-table-cell'>{missingData()}</span>
        )
      },
    },
    {
      key: "Timestamp",
      Header: "Timestamp",
      id: "timestamp",
      filterable: false,
      // width: 200,
      accessor: data => {
        const uid = data?.uid;
        const date = new Date(uid * 1000);
        const timestamp = date.toLocaleString();
        return (
          timestamp ?
            <span className='data-table-cell'>{timestamp}</span>
          :
            <span className='data-table-cell'>{missingData()}</span>
        )
      },
    },
    {
      key: "UID",
      Header: "UID",
      id: "uid",
      // width: 100,
      accessor: data => {
        const uid = data?.uid;
        return (
          <span className='data-table-cell'>{uid}</span>
        )
      },
    },
    {
      key: "Status",
      Header: "Status",
      id: "status",
      filterable: false,
      // width: 200,
      accessor: data => {
        const status = data?.status;

        let currentStatus = "Status unavailable";
        let currentIcon = {};
        
        const percentage = allPercentages.find(i => i?.uid === data?.uid)?.percentage ?? null;

        switch (status) {
          case "C":
            currentStatus = "Completed";
            currentIcon = {
              icon:  "check-circle",
              colour: "green",
              description: 'Completed successfully',
            };
            break;
          
          case "CE":
            currentStatus = "Completed";
            currentIcon = {
              icon:  "exclamation-circle",
              colour: "red",
              description: 'Completed with errors',
            };
            break;

          case "N":
            currentStatus = allInProgress?.find(p => p?.uid === data?.uid) ? "In Progress" : "Waiting...";
            break;

          case null:
            currentStatus = "Status unavailable";
            break;
        
          default:
            break;
        }

        return (
          <span className='data-table-cell'>
            {/* status text */}
            {windowSize.width < 1100 && status === "N" && !allInQueue?.find(p => p?.uid === data?.uid) ? null : <span className='mr-2'>{currentStatus}</span>}
            
            {/* show progress bar or other icon */}
            {status === 'N' && allInProgress?.find(p => p?.uid === data?.uid) ?
              // show progress bar if uid present or fallback icon if not
              // uid ?
                <CustomProgressBar show={true} value={percentage ?? 0} styling='mt-1 custom-width' tooltip={true} active={status === "N"} />
            :
              // other icons (e.g. complete)
              <Tooltip
                placement='top'
                description={currentIcon.description}
                white={!darkMode}
              >
                <Icon className={`fas fa-${currentIcon.icon}`} style={{ color: currentIcon.colour }} />
              </Tooltip>
            }
          </span>
        )
      },
    },
    {
      key: "Actions",
      Header: "Actions",
      id: "actions",
      filterable: false,
      width: 100,
      accessor: data => data?.uid,
      Cell: ({row}) => {
        return <div>
          {
            <>
              <Tooltip
                placement='top'
                description='View More'
              >
                <Button
                  variant='primary' 
                  className='d-flex justify-content-center align-items-center'
                  onClick={() => {
                    row?.actions && setViewUid(row?.actions);
                    // setinitialiseLog(true);
                    handleShowModal();
                  }}
                  style={{
                    margin: "0 8px",
                    padding: "0px",
                    width: "50px",
                    height: "29px",
                  }}
                >
                  <Icon className='fas fa-eye' title='View' />
                </Button> 
              </Tooltip>
            </>
          }
        </div>
      }
    },
  ];

  return (
    <div className={`${darkMode ? "darkMode" : ""} h-100 asset-logs-container`}>

      <Card className="card-wrapper">

        <header id='asset-logs-header'>
          <Row className='d-flex justify-content-between align-items-center pt-2'>
            <Col xs={4} md={4} lg={4} xl={4} className='w-100 pl-3 d-flex justify-content-start'>
              <Link 
                to={`/companies/company/${companyId}/assets`} 
                className="back-to-sd d-flex align-items-center"
              >
                <Icon className="fas fa-chevron-left mr-2" />
                <p className="m-0">Back to Assets</p>
              </Link>
            </Col>
            <Col xs={4} md={4} lg={4} xl={4} className='w-100 d-flex justify-content-center' id='asset-logs-name'>
              Company Asset Logs
            </Col>
            <Col xs={4} md={4} lg={4} xl={4} className='w-100 pr-3 d-flex justify-content-end'>
              {`Total Logs: ${totalLogs}`}
            </Col>
          </Row>
        </header>

        <main className='assets-logs-table-wrapper'>
          {/* overall progress section */}
          <div>
            <Row id='progress-section-container'>
              <Col xs={3} md={3} lg={3} xl={3}>
                <Row className='w-100'>
                  <Col xs={6} md={6} lg={6} xl={6} className='d-flex justify-content-start'>
                    <Countdown 
                      setTriggerCall={setTriggerCall} 
                      countValue={countValue} 
                      activeProcess={activeProcess} 
                      delayCall={delayCall} 
                      setDelayCall={setDelayCall} 
                      progressArray={flatProgresses}
                    />
                  </Col>
                  {/* <Col xs={6} md={6} lg={6} xl={6} className='d-flex justify-content-end'>
                    <span className='overall-status w-100 px-0'>Overall Status:</span>
                  </Col> */}
                </Row>
              </Col>

              <Col xs={9} md={9} lg={9} xl={9} className='d-flex align-items-center'>
                <Row className='w-100 progress-sections-cols'>
                    {/* imports */}
                    <Col xs={4} md={4} lg={4} xl={4} className='d-flex justify-content-start progress-col'>
                      <ProgressSection
                        hide={delayCall}
                        enableQueueWrapper="true"
                        queue={importQueued}
                        title={'Import'}
                        usePercentage="true"
                        percentageVal={allPercentages.find(p => p.type === 'import')?.percentage}
                        active={importProgress.length > 0}
                      />
                    </Col>

                    {/* exports */}
                    <Col xs={4} md={4} lg={4} xl={4} className='d-flex justify-content-center progress-col'>
                      <ProgressSection
                        hide={delayCall}
                        enableQueueWrapper="true"
                        queue={exportQueued}
                        title={'Export'}
                        usePercentage="true"
                        percentageVal={allPercentages.find(p => p.type === 'export')?.percentage}
                        active={exportProgress.length > 0}
                      />
                    </Col>

                    {/* bulk delete */}
                    <Col xs={4} md={4} lg={4} xl={4} className='d-flex justify-content-end progress-col'>
                      <ProgressSection
                        hide={delayCall}
                        enableQueueWrapper="true"
                        queue={deleteQueued}
                        title={'Delete'}
                        usePercentage="true"
                        percentageVal={allPercentages.find(p => p.type === 'delete')?.percentage}
                        active={deleteProgress.length > 0}
                      />
                    </Col>
                  {/* </Col> */}
                </Row>
              </Col>
              
            </Row>
          </div>

          <DataTableWrapper
            id="company-assets-logs-index-table"
            columns={columnHeaders}
            data={logsIndexData}
            pages={pagination && pagination.pageCount}
            loadDataAction={loadCompanyAssetLogsStart}
            noDataText={"No logs have been found"}
          />

        </main>
      </Card>
      
      {viewUid &&
        <LogViewPage
          uid={viewUid}
          show={showLogViewPage}
          hide={!showLogViewPage}
          hideModal={handleHideModal}
          setViewUid={setViewUid}
          status={viewUid && uidStatus[viewUid]}
          percentage={viewPercentage}
          triggerCall={triggerCall}
          // initialiseLog={initialiseLog}
          // setinitialiseLog={setinitialiseLog}
          dataTableState={dataTableState}
          handleStorePaginationState={handleStorePaginationState}
        />
      }

    </div>
  )
}

export default CompanyAssetLogs;
